/**
 * client/src/components/forensic-recovery-lift-questions.tsx
 *
 * #3042 Part B item 3 — the ward (requester) leg's question-gated answer
 * step. Reuses `QuestionAnswerer` once per seated guardian (Reuse-First,
 * `MutualChallengePanel` is not a fit here: it self-fetches and self-mints a
 * nonce per single subject, whereas the ward leg needs ONE shared nonce
 * across MULTIPLE guardians). Stays network-free itself — `onBuildChallenge`
 * is injected by the caller (mirrors `ForensicRecoveryDialog`'s existing
 * "dumb presentational component" pattern), so this component neither fetches
 * nor mints anything.
 *
 * Honesty: if any seated guardian has not enrolled verification questions
 * (server-side signal: `questions.length === 0`,
 * server/logic/guardian/mutual-challenge-lift-questions.ts), this step says
 * so plainly in amber and renders no answer/submit path at all -- partial
 * proofs would fail server-side anyway since the seated guardian set is
 * fixed, and CLAUDE.md forbids liveness/"verified human" framing on this
 * surface.
 *
 * Failure handling (adversarial review round 1, MAJOR 5/MAJOR 6):
 * `onBuildChallenge` mints a fresh nonce and re-hashes every call
 * (`prepareWardLiftChallenge`), so a 429/403/network failure here is caught,
 * shown in amber, and retried by simply pressing Check my answers again -- no stale
 * nonce risk. `onChooseDifferentMethod`, when supplied, lets the ward back
 * out of this step entirely rather than being stuck if the seated guardian
 * set is unusable (e.g. every guardian unenrolled).
 */

import { useState } from "react";

import { Button } from "@/components/ui/button";
import { QuestionAnswerer } from "@/pages/guardian-ceremony/virtual/components/QuestionAnswerer";
import type { WardLiftChallenge, WardLiftGuardianQuestions } from "@/lib/forensic-recovery-lift-challenge";

export interface ForensicRecoveryLiftQuestionsProps {
  readonly guardians: readonly WardLiftGuardianQuestions[];
  readonly busy?: boolean;
  readonly onBuildChallenge: (
    rawAnswersByConnection: Record<string, string[]>,
  ) => Promise<WardLiftChallenge>;
  readonly onSubmit: (challenge: WardLiftChallenge) => void;
  readonly onChooseDifferentMethod?: () => void;
}

export function ForensicRecoveryLiftQuestions(
  { guardians, busy = false, onBuildChallenge, onSubmit, onChooseDifferentMethod }: ForensicRecoveryLiftQuestionsProps,
): JSX.Element {
  const [answersByConnection, setAnswersByConnection] = useState<Record<string, string[]>>({});
  const [building, setBuilding] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);

  const unenrolled = guardians.filter((g) => g.questions.length === 0);
  const answerable = guardians.filter((g) => g.questions.length > 0);

  if (unenrolled.length > 0) {
    return (
      <div role="alert" className="space-y-4 text-warning">
        {unenrolled.map((guardian) => (
          <p key={guardian.connectionId}>
            {guardian.guardianLabel} has not set up verification questions yet, so this method cannot continue right now.
          </p>
        ))}
        {onChooseDifferentMethod ? (
          <Button type="button" variant="secondary" onClick={onChooseDifferentMethod}>
            Choose a different method
          </Button>
        ) : null}
      </div>
    );
  }

  const answeredCount = answerable.filter((g) => answersByConnection[g.connectionId]).length;
  const allAnswered = answerable.length > 0 && answeredCount >= answerable.length;
  const isBusy = busy || building;

  async function handleContinue(): Promise<void> {
    if (!allAnswered || isBusy) return;
    setBuilding(true);
    setBuildError(null);
    try {
      const challenge = await onBuildChallenge(answersByConnection);
      onSubmit(challenge);
    } catch {
      // `onBuildChallenge` mints a fresh nonce every call, so retrying is
      // just pressing Check my answers again -- no stale-nonce cleanup needed here.
      setBuildError("Could not verify those answers. Check your connection and try again.");
    } finally {
      setBuilding(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {answerable.map((guardian) => (
        <div key={guardian.connectionId} className="rounded-lg border border-border-default p-4">
          <h4 className="mb-2 font-semibold">{guardian.guardianLabel}</h4>
          <QuestionAnswerer
            busy={isBusy}
            questions={guardian.questions.map((q) => ({ questionOrder: q.order, questionText: q.text }))}
            onSubmit={(answers) => {
              setAnswersByConnection((prev) => ({ ...prev, [guardian.connectionId]: answers }));
            }}
          />
        </div>
      ))}
      {buildError ? <p role="alert" className="text-warning">{buildError}</p> : null}
      <div className="flex flex-wrap gap-3">
        <Button
          type="button"
          variant="primaryAccessible"
          loading={building}
          disabled={!allAnswered || isBusy}
          onClick={() => { void handleContinue(); }}
        >
          Check my answers
        </Button>
        {onChooseDifferentMethod ? (
          <Button type="button" variant="secondary" disabled={isBusy} onClick={onChooseDifferentMethod}>
            Choose a different method
          </Button>
        ) : null}
      </div>
    </div>
  );
}
